import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  FaBold,
  FaItalic,
  FaQuoteRight,
  FaListUl,
  FaListOl,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaImage,
  FaLink,
  FaUnderline,
  FaStrikethrough,
  FaAlignJustify,
  FaUndo,
  FaRedo,
  FaUpload,
} from "react-icons/fa";
import { BiCodeBlock, BiSolidSun } from "react-icons/bi";
import { MdFontDownload } from "react-icons/md";
import { AiFillMoon } from "react-icons/ai";
import "./RichTextEditor.css";
import IconComponent from "../common/IconComponent";
import { ComboBox } from "@progress/kendo-react-dropdowns";

interface RichTextEditorProps {
  content: string;
  onChange: (value: string) => void;
  maxHeight?: number;
  minHeight?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  maxHeight = 500,
  minHeight = 200,
}) => {
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [editorHeight, setEditorHeight] = useState(minHeight);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    align: "left",
    formatBlock: "p",
    orderedList: false,
    unorderedList: false,
  });
  const [isUploading, setIsUploading] = useState(false);

  const editorRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef(-1);
  const isUpdatingContentRef = useRef(false);
  const lastContentRef = useRef("");
  const selectionRef = useRef<Range | null>(null);
  const isResizingRef = useRef(false);

  const headingOptions = [
    { value: "p", label: "Paragraph", size: "14px" },
    { value: "h1", label: "Heading 1", size: "32px" },
    { value: "h2", label: "Heading 2", size: "24px" },
    { value: "h3", label: "Heading 3", size: "20px" },
    { value: "h4", label: "Heading 4", size: "18px" },
    { value: "h5", label: "Heading 5", size: "16px" },
    { value: "h6", label: "Heading 6", size: "14px" },
  ];

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle image upload
  const handleImageUpload = async (file: File) => {
    try {
      setIsUploading(true);

      // Validate file size (max 5MB)
      if (file.size > 1 * 1024 * 1024) {
        alert("Image size must be less than 1MB");
        return null;
      }

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/svg+xml",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Only image files are allowed (JPEG, PNG, GIF, WEBP, SVG)");
        return null;
      }

      // Convert to base64
      const base64Data = await fileToBase64(file);
      return base64Data;
    } catch (error) {
      console.error("Error converting image:", error);
      alert("Failed to upload image");
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const updateActiveFormats = useCallback(() => {
    if (isHtmlMode || !editorRef.current || isUpdatingContentRef.current)
      return;

    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);
      if (!editorRef.current.contains(range.commonAncestorContainer)) return;

      // Check text formats
      const newFormats = {
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikethrough: document.queryCommandState("strikeThrough"),
        orderedList: document.queryCommandState("insertOrderedList"),
        unorderedList: document.queryCommandState("insertUnorderedList"),
        align: "left" as string,
        formatBlock: "p" as string,
      };

      // Check alignment
      if (document.queryCommandState("justifyCenter")) {
        newFormats.align = "center";
      } else if (document.queryCommandState("justifyRight")) {
        newFormats.align = "right";
      } else if (document.queryCommandState("justifyFull")) {
        newFormats.align = "justify";
      } else {
        newFormats.align = "left";
      }

      // Check block format
      let element =
        range.commonAncestorContainer.nodeType === 3
          ? range.commonAncestorContainer.parentElement
          : (range.commonAncestorContainer as HTMLElement);

      while (element && element !== editorRef.current) {
        const tagName = element.tagName?.toLowerCase();
        if (
          ["h1", "h2", "h3", "h4", "h5", "h6", "p", "blockquote"].includes(
            tagName,
          )
        ) {
          newFormats.formatBlock = tagName;
          break;
        }
        // Also check for list items
        if (tagName === "li") {
          const listParent = element.parentElement;
          if (listParent?.tagName.toLowerCase() === "ol") {
            newFormats.orderedList = true;
          } else if (listParent?.tagName.toLowerCase() === "ul") {
            newFormats.unorderedList = true;
          }
        }
        element = element.parentElement;
      }

      setActiveFormats(newFormats);
    } catch (error) {
      console.warn("Error updating formats:", error);
    }
  }, [isHtmlMode]);

  // Handle input events
  const handleInput = useCallback(() => {
    if (!editorRef.current || isUpdatingContentRef.current) return;

    const currentContent = editorRef.current.innerHTML;

    if (lastContentRef.current !== currentContent) {
      lastContentRef.current = currentContent;

      // Add to history
      if (historyRef.current[historyIndexRef.current] !== currentContent) {
        if (historyIndexRef.current < historyRef.current.length - 1) {
          historyRef.current = historyRef.current.slice(
            0,
            historyIndexRef.current + 1,
          );
        }

        historyRef.current.push(currentContent);
        historyIndexRef.current = historyRef.current.length - 1;

        if (historyRef.current.length > 50) {
          historyRef.current.shift();
          historyIndexRef.current--;
        }
      }

      onChange(currentContent);
      updateActiveFormats();
    }
  }, [onChange, updateActiveFormats]);

  // Insert image into editor
  const insertImage = useCallback(
    async (imageData: string, altText: string = "Image") => {
      if (!editorRef.current) return;

      // Ensure editor has focus
      editorRef.current.focus();

      // Restore selection if available
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        // Create image element
        const img = document.createElement("img");
        img.src = imageData;
        img.alt = altText;
        img.style.maxWidth = "100%";
        img.style.height = "auto";
        img.style.margin = "10px 0";

        // Insert image at cursor position
        range.insertNode(img);

        // Move cursor after the image
        range.setStartAfter(img);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);

        // Trigger input event
        handleInput();
      } else {
        // If no selection, append to end
        const img = document.createElement("img");
        img.src = imageData;
        img.alt = altText;
        img.style.maxWidth = "100%";
        img.style.height = "auto";
        img.style.margin = "10px 0";

        editorRef.current.appendChild(img);
        handleInput();
      }
    },
    [handleInput],
  );

  // Handle image upload via file dialog
  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        const base64Data = await handleImageUpload(file);

        if (base64Data) {
          insertImage(base64Data, file.name);
        }

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [insertImage],
  );

  // Handle paste event for images
  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      if (isHtmlMode || !editorRef.current) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf("image") !== -1) {
          e.preventDefault();

          const file = item.getAsFile();
          if (file) {
            // Show uploading indicator
            setIsUploading(true);

            fileToBase64(file)
              .then((base64Data) => {
                if (base64Data) {
                  insertImage(base64Data, "pasted-image");
                }
                setIsUploading(false);
              })
              .catch((error) => {
                console.error("Failed to process pasted image:", error);
                setIsUploading(false);
              });
          }
          break;
        }
      }
    },
    [isHtmlMode, insertImage],
  );

  // Handle drop event for images
  const handleDrop = useCallback(
    (e: DragEvent) => {
      if (isHtmlMode || !editorRef.current) return;

      e.preventDefault();

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (file.type.startsWith("image/")) {
            // Show uploading indicator
            setIsUploading(true);

            fileToBase64(file)
              .then((base64Data) => {
                if (base64Data) {
                  insertImage(base64Data, file.name);
                }
                setIsUploading(false);
              })
              .catch((error) => {
                console.error("Failed to process dropped image:", error);
                setIsUploading(false);
              });
            break;
          }
        }
      }
    },
    [isHtmlMode, insertImage],
  );

  // Update existing image button click handler
  const handleImageButtonClick = () => {
    // First try to use existing URL method
    const url = prompt(
      "Enter image URL or click OK to upload from your computer:",
    );

    if (url === null) return; // User cancelled

    if (url === "") {
      // User wants to upload - trigger file input
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } else if (url.trim() !== "") {
      // User entered a URL - use existing method
      exec("insertImage", url.trim());
    }
  };

  // Execute command with focus management
  const exec = useCallback(
    (command: string, value?: string) => {
      if (!editorRef.current) return;

      // Ensure editor has focus
      editorRef.current.focus();

      // Restore selection if needed
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }

      try {
        const result = document.execCommand(command, false, value);
        console.log(`Command ${command} executed:`, result);

        // Force update after command
        setTimeout(() => {
          handleInput();
          updateActiveFormats();
        }, 10);
      } catch (error) {
        console.warn("Command execution failed:", error);
      }
    },
    [handleInput, updateActiveFormats],
  );

  // Handle content change for textarea
  const handleTextareaChange = useCallback(() => {
    const value = textareaRef.current?.value || "";
    onChange(value);
  }, [onChange]);

  // Undo functionality
  const undo = useCallback(() => {
    if (historyIndexRef.current > 0 && editorRef.current) {
      isUpdatingContentRef.current = true;
      historyIndexRef.current--;
      editorRef.current.innerHTML = historyRef.current[historyIndexRef.current];
      lastContentRef.current = editorRef.current.innerHTML;
      onChange(editorRef.current.innerHTML);

      setTimeout(() => {
        isUpdatingContentRef.current = false;
        updateActiveFormats();
      }, 10);
    }
  }, [onChange, updateActiveFormats]);

  // Redo functionality
  const redo = useCallback(() => {
    if (
      historyIndexRef.current < historyRef.current.length - 1 &&
      editorRef.current
    ) {
      isUpdatingContentRef.current = true;
      historyIndexRef.current++;
      editorRef.current.innerHTML = historyRef.current[historyIndexRef.current];
      lastContentRef.current = editorRef.current.innerHTML;
      onChange(editorRef.current.innerHTML);

      setTimeout(() => {
        isUpdatingContentRef.current = false;
        updateActiveFormats();
      }, 10);
    }
  }, [onChange, updateActiveFormats]);

  // Toggle HTML mode
  const toggleHtml = useCallback(() => {
    setIsHtmlMode((prev) => !prev);
    if (!isHtmlMode) {
      setTimeout(() => handleTextareaChange(), 0);
    } else {
      setTimeout(() => handleInput(), 0);
    }
  }, [isHtmlMode, handleTextareaChange, handleInput]);

  // Toggle Theme
  const toggleTheme = useCallback(() => {
    setIsDarkMode((prev) => !prev);
  }, []);

  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      selectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (selectionRef.current && editorRef.current) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(selectionRef.current);
      editorRef.current.focus();
    }
  }, []);

  const handleHeadingSelect = useCallback(
    (value: string) => {
      restoreSelection();
      exec("formatBlock", `<${value}>`);
    },
    [exec, restoreSelection],
  );

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === "y") {
        e.preventDefault();
        redo();
        return;
      }
    },
    [undo, redo],
  );

  // Check if undo/redo is available
  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  // Initialize content
  useEffect(() => {
    if (!isHtmlMode && editorRef.current && !isUpdatingContentRef.current) {
      const initialContent = content || "<p><br></p>";

      if (editorRef.current.innerHTML !== initialContent) {
        isUpdatingContentRef.current = true;
        editorRef.current.innerHTML = initialContent;

        if (historyRef.current.length === 0) {
          historyRef.current = [initialContent];
          historyIndexRef.current = 0;
        }

        lastContentRef.current = initialContent;

        setTimeout(() => {
          isUpdatingContentRef.current = false;
        }, 50);
      }
    }

    if (isHtmlMode && textareaRef.current) {
      if (textareaRef.current.value !== content) {
        textareaRef.current.value = content || "";
      }
    }
  }, [isHtmlMode, content]);

  // Handle selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      if (!isUpdatingContentRef.current) {
        updateActiveFormats();
      }
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", handleSelectionChange);
  }, [updateActiveFormats]);

  // Set up event listeners
  useEffect(() => {
    const editor = editorRef.current;
    if (!isHtmlMode && editor) {
      editor.addEventListener("input", handleInput);
      editor.addEventListener("keydown", handleKeyDown);

      // Add paste and drop event listeners - remove the type casting
      editor.addEventListener("paste", handlePaste);
      editor.addEventListener("drop", handleDrop);
      editor.addEventListener("dragover", (e) => {
        e.preventDefault(); // Required for drop to work
      });

      return () => {
        editor.removeEventListener("input", handleInput);
        editor.removeEventListener("keydown", handleKeyDown);
        editor.removeEventListener("paste", handlePaste);
        editor.removeEventListener("drop", handleDrop);
      };
    }
  }, [isHtmlMode, handleInput, handleKeyDown, handlePaste, handleDrop]);

  // Handle resize functionality
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeHandle = containerRef.current.querySelector(
      ".resize-handle",
    ) as HTMLElement;
    if (!resizeHandle) return;

    let startY = 0;
    let startHeight = 0;

    const handleMouseDown = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      mouseEvent.preventDefault();
      isResizingRef.current = true;
      startY = mouseEvent.clientY;
      startHeight = editorHeight;
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ns-resize";
    };

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      if (!isResizingRef.current) return;
      const deltaY = mouseEvent.clientY - startY;
      const newHeight = Math.min(
        Math.max(startHeight + deltaY, minHeight),
        maxHeight,
      );
      setEditorHeight(newHeight);
    };

    const handleMouseUp = () => {
      isResizingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
    };

    resizeHandle.addEventListener("mousedown", handleMouseDown);
    return () => {
      resizeHandle.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [editorHeight, maxHeight, minHeight]);

  return (
    <div
      className="rich-text-editor"
      ref={containerRef}
      style={{ height: `${editorHeight + 60}px` }} // 60px for toolbar height
    >
      {/* Hidden file input for image uploads */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {/* Uploading overlay */}
      {isUploading && (
        <div
          className="uploading-overlay"
          style={{
            position: "absolute",
            top: "60px", // Below toolbar
            left: 0,
            right: 0,
            bottom: "20px", // Above resize handle
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            borderRadius: "4px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div className="k-loading-spinner k-spin">
              <span className="k-loading-spinner-sm"></span>
            </div>
            <div style={{ color: "#666", fontSize: "14px" }}>
              Uploading image...
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Toolbar */}
      <div className="toolbar">
        {/* Heading Dropdown */}
        <div className="toolbar-group">
          <div className="dropdown-container">
            <ComboBox
              data={headingOptions}
              textField="label"
              value={headingOptions.find(
                (option) => option.value === activeFormats.formatBlock,
              )}
              onChange={(e) => {
                const selectedValue = e.value?.value || "p";
                handleHeadingSelect(selectedValue);
              }}
              onOpen={() => {
                saveSelection();
              }}
              onClose={() => {
                setTimeout(restoreSelection, 10);
              }}
              filterable={false}
              size={"small"}
              style={{ width: "140px" }}
            />
          </div>
        </div>
        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => exec("bold")}
            className={`toolbar-btn ${activeFormats.bold ? "active" : ""}`}
            title="Bold"
          >
            <IconComponent icon={FaBold} className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("italic")}
            className={`toolbar-btn ${activeFormats.italic ? "active" : ""}`}
            title="Italic"
          >
            <IconComponent icon={FaItalic} className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("underline")}
            className={`toolbar-btn ${activeFormats.underline ? "active" : ""}`}
            title="Underline"
          >
            <IconComponent icon={FaUnderline} className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("strikeThrough")}
            className={`toolbar-btn ${
              activeFormats.strikethrough ? "active" : ""
            }`}
            title="Strikethrough"
          >
            <IconComponent icon={FaStrikethrough} className="icon" />
          </button>
        </div>
        <div className="toolbar-divider"></div>

        {/* Lists */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => exec("insertOrderedList")}
            className={`toolbar-btn ${
              activeFormats.orderedList ? "active" : ""
            }`}
            title="Numbered List"
          >
            <IconComponent icon={FaListOl} className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("insertUnorderedList")}
            className={`toolbar-btn ${
              activeFormats.unorderedList ? "active" : ""
            }`}
            title="Bulleted List"
          >
            <IconComponent icon={FaListUl} className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("formatBlock", "<blockquote>")}
            className={`toolbar-btn ${
              activeFormats.formatBlock === "blockquote" ? "active" : ""
            }`}
            title="Blockquote"
          >
            <IconComponent icon={FaQuoteRight} className="icon" />
          </button>
        </div>
        <div className="toolbar-divider"></div>

        {/* Alignment */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => exec("justifyLeft")}
            className={`toolbar-btn ${
              activeFormats.align === "left" ? "active" : ""
            }`}
            title="Align Left"
          >
            <IconComponent icon={FaAlignLeft} className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("justifyCenter")}
            className={`toolbar-btn ${
              activeFormats.align === "center" ? "active" : ""
            }`}
            title="Align Center"
          >
            <IconComponent icon={FaAlignCenter} className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("justifyRight")}
            className={`toolbar-btn ${
              activeFormats.align === "right" ? "active" : ""
            }`}
            title="Align Right"
          >
            <IconComponent icon={FaAlignRight} className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("justifyFull")}
            className={`toolbar-btn ${
              activeFormats.align === "justify" ? "active" : ""
            }`}
            title="Justify"
          >
            <IconComponent icon={FaAlignJustify} className="icon" />
          </button>
        </div>
        <div className="toolbar-divider"></div>

        {/* Insert buttons - Modified Image button */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => {
              const url = prompt("Enter link URL:");
              if (url) exec("createLink", url);
            }}
            className="toolbar-btn"
            title="Insert Link"
          >
            <IconComponent icon={FaLink} className="icon" />
          </button>

          <button
            type="button"
            onClick={handleImageButtonClick}
            className="toolbar-btn"
            title="Insert Image (URL or Upload)"
          >
            <IconComponent icon={FaImage} className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("insertHorizontalRule")}
            className="toolbar-btn"
            title="Insert Horizontal Rule"
          >
            <IconComponent icon={BiCodeBlock} className="icon" />
          </button>

          {/* Optional: Direct upload button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="toolbar-btn"
            title="Upload Image from Computer"
            disabled={isUploading}
          >
            <IconComponent icon={FaUpload} className="icon" />
          </button>
        </div>
        <div className="toolbar-divider"></div>

        {/* Undo/Redo */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={undo}
            className={`toolbar-btn ${!canUndo ? "disabled" : ""}`}
            disabled={!canUndo}
            title="Undo"
          >
            <IconComponent icon={FaUndo} className="icon" />
          </button>

          <button
            type="button"
            onClick={redo}
            className={`toolbar-btn ${!canRedo ? "disabled" : ""}`}
            disabled={!canRedo}
            title="Redo"
          >
            <IconComponent icon={FaRedo} className="icon" />
          </button>
        </div>
        <div className="toolbar-divider"></div>

        {/* Toggle HTML */}
        <button
          type="button"
          onClick={toggleHtml}
          className="html-toggle-btn"
          title={isHtmlMode ? "Switch to Rich Text" : "Switch to HTML"}
        >
          {isHtmlMode ? (
            <>
              <IconComponent icon={MdFontDownload} className="icon" />
              Rich Text
            </>
          ) : (
            <>
              <IconComponent icon={BiCodeBlock} className="icon" />
              HTML
            </>
          )}
        </button>

        <button
          type="button"
          onClick={toggleTheme}
          className={`toolbar-btn ${isDarkMode ? "active" : ""}`}
          title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {isDarkMode ? (
            <>
              <IconComponent icon={BiSolidSun} className="icon" />
            </>
          ) : (
            <>
              <IconComponent icon={AiFillMoon} className="icon" />
            </>
          )}
        </button>
      </div>

      {/* Editor */}
      <div className="editor-area" style={{ height: `${editorHeight}px` }}>
        {isHtmlMode ? (
          <textarea
            ref={textareaRef}
            defaultValue={content}
            onChange={handleTextareaChange}
            className="html-textarea"
            placeholder="Enter your HTML content here..."
          />
        ) : (
          <div
            ref={editorRef}
            className={`rich-text-content ${
              isDarkMode ? "bg-gray" : "bg-white"
            }`}
            contentEditable
            suppressContentEditableWarning
            style={{
              cursor: isUploading ? "wait" : "text",
              opacity: isUploading ? 0.7 : 1,
            }}
          />
        )}
      </div>

      {/* Resize Handle */}
      <div className="resize-handle" title="Drag to resize"></div>

      {/* Image upload hint */}
      {/* {!isHtmlMode && (
        <div
          className="upload-hint"
          style={{
            fontSize: "12px",
            color: "#666",
            padding: "5px 10px",
            borderTop: "1px solid #eee",
            backgroundColor: "#f9f9f9",
          }}
        >
          💡 Tip: You can drag & drop images, paste from clipboard, or use the
          upload button
        </div>
      )} */}
    </div>
  );
};

export default RichTextEditor;
